import * as React from "react";
// eslint-disable-next-line import/no-restricted-paths

import { cn } from "@/lib/chillnn/cn";
import { sanitize } from "@/lib/dompurify";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card/hover-card";

import Help from "../icon/help";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
	/**
	 * tableタグをラップするタグに対するclassName
	 * heightを指定する場合などに使用する
	 */
	tableWrapperClassName?: string;
}

const TABLE_ROOT_ID = "TABLE_ROOT_ID";

// const useIsScrollableHorizontally = (id: string) => {
// 	const { width } = useWindowSize();
// 	const [isScrollable, setIsScrollable] = React.useState(false);

// 	React.useEffect(() => {
// 		const tableEl = document.getElementById(id);
// 		const tableChildEl = tableEl?.children[0];

// 		if (tableChildEl && tableChildEl.clientWidth > tableEl.clientWidth) {
// 			setIsScrollable(true);
// 		} else {
// 			setIsScrollable(false);
// 		}
// 	}, [id, width]);

// 	return { isScrollable };
// };

const Table = React.forwardRef<HTMLTableElement, TableProps>(
	({ className, tableWrapperClassName = "", id = TABLE_ROOT_ID, ...props }, ref) => {
		// const { isScrollable } = useIsScrollableHorizontally(id);

		return (
			<div>
				{/* {isScrollable && (
					<div className="flex items-center justify-end gap-x-2">
						<SwipeIcon />
						<p className="text-sm">左右にスクロールできます</p>
					</div>
				)} */}
				<div className={cn("relative w-full overflow-auto", tableWrapperClassName)} id={id}>
					<table
						ref={ref}
						className={cn("w-full min-w-[min-content] caption-top text-sm", className)}
						{...props}
					/>
				</div>
			</div>
		);
	},
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<thead ref={ref} className={cn("[&_tr]:border-b [&_tr]:border-zinc-300", className)} {...props} />
	),
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
	),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<tfoot
			ref={ref}
			className={cn("border-t bg-zinc-100/50 font-medium [&>tr]:last:border-b-0", className)}
			{...props}
		/>
	),
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
	({ className, ...props }, ref) => (
		<tr
			ref={ref}
			className={cn(
				"border-b border-zinc-200 transition-colors hover:bg-zinc-100/50 data-[state=selected]:bg-zinc-100",
				className,
			)}
			{...props}
		/>
	),
);
TableRow.displayName = "TableRow";

interface TableHeadProps extends React.HTMLAttributes<HTMLTableCellElement> {
	guideText?: string;
	justifyStyle?: "start" | "center" | "end";
	colSpan?: number;
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
	({ className, guideText = "", justifyStyle = "start", colSpan = 1, ...props }, ref) => {
		const justifyClassName = React.useMemo(
			() =>
				justifyStyle === "start"
					? "gap-x-2"
					: justifyStyle === "center"
						? "justify-center"
						: "gap-x-2 justify-end",
			[justifyStyle],
		);

		return (
			<th
				ref={ref}
				className={cn("h-12 px-4 text-left align-middle font-medium text-zinc-500", className)}
				colSpan={colSpan}
				{...props}
			>
				<div className={cn("flex items-center", justifyClassName)}>
					<h6>{props.children}</h6>

					{Boolean(guideText) && (
						<HoverCard>
							<HoverCardTrigger className="inline-block rounded-full hover:bg-zinc-100">
								<Help className="h-[18px] w-[18px]" />
							</HoverCardTrigger>
							<HoverCardContent side={"top"} align={"center"} sideOffset={18} className="w-96">
								<p dangerouslySetInnerHTML={{ __html: sanitize(guideText) }} className="min-h-[18px]" />
							</HoverCardContent>
						</HoverCard>
					)}
				</div>
			</th>
		);
	},
);
TableHead.displayName = "TableHead";

interface TableCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
	zeroPadding?: boolean;
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
	({ className, zeroPadding = false, ...props }, ref) => {
		const padding = zeroPadding ? "p-0" : "p-4";

		return (
			<td ref={ref} className={cn("align-middle [&:has([role=checkbox])]:pr-0", className, padding)} {...props} />
		);
	},
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
	({ className, ...props }, ref) => (
		<caption
			ref={ref}
			className={cn("my-4 text-left text-lg font-semibold tracking-widest text-zinc-950", className)}
			{...props}
		/>
	),
);
TableCaption.displayName = "TableCaption";

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
